import React from 'react';
import {
  LogIn, LogOut, XCircle, Lock, Unlock, Key, Shield, Clock, ExternalLink, History, MapPin, Settings, Radio, ShieldAlert,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { getAuditActionLabel } from '../../utils/auditActions';

/**
 * Reusable audit-log renderer. Used by the Settings → Sub Account "Audit" tab
 * (and reusable by any other surface that lists `audit_logs` entries).
 *
 * Props:
 *  - logs: array of audit log docs ({ id, action, details, actor_email, ip_address, timestamp, changes })
 *  - formatTimestamp: (ts) => string  — timezone-aware formatter (pass the page's helper)
 *  - loading: boolean
 *  - emptyText / emptySubText: empty-state copy
 */
const iconMeta = (action) => {
  switch (action) {
    case 'user_login': return { Icon: LogIn, cls: 'bg-green-100 text-green-600' };
    case 'user_login_failed': return { Icon: XCircle, cls: 'bg-red-100 text-red-600' };
    case 'user_logout': return { Icon: LogOut, cls: 'bg-slate-100 text-slate-600' };
    case 'session_expired': return { Icon: Clock, cls: 'bg-slate-100 text-slate-600' };
    case 'session_ended_inactive': return { Icon: Clock, cls: 'bg-slate-100 text-slate-600' };
    case 'session_resumed': return { Icon: LogIn, cls: 'bg-emerald-100 text-emerald-600' };
    case 'user_blocked': return { Icon: Lock, cls: 'bg-red-100 text-red-600' };
    case 'user_unblocked': return { Icon: Unlock, cls: 'bg-green-100 text-green-600' };
    case 'password_changed': return { Icon: Key, cls: 'bg-amber-100 text-amber-600' };
    case 'role_changed': return { Icon: Shield, cls: 'bg-purple-100 text-purple-600' };
    case 'expiry_changed': return { Icon: Clock, cls: 'bg-orange-100 text-orange-600' };
    case 'user_impersonated': return { Icon: ExternalLink, cls: 'bg-blue-100 text-blue-600' };
    case 'impersonation_ended': return { Icon: LogOut, cls: 'bg-blue-100 text-blue-600' };
    case 'trusted_device_removed':
    case 'trusted_network_removed': return { Icon: ShieldAlert, cls: 'bg-orange-100 text-orange-600' };
    case 'sessions_purged': return { Icon: LogOut, cls: 'bg-orange-100 text-orange-600' };
    case 'geofence_created':
    case 'geofence_updated':
    case 'geofence_deleted': return { Icon: MapPin, cls: 'bg-teal-100 text-teal-600' };
    case 'global_settings_changed': return { Icon: Settings, cls: 'bg-indigo-100 text-indigo-600' };
    case 'command_sent': return { Icon: Radio, cls: 'bg-amber-100 text-amber-600' };
    default: return { Icon: History, cls: 'bg-slate-100 text-slate-600' };
  }
};

export const getAuditActionIcon = iconMeta;

const SECURITY_FLAG_LABELS = { new_ip: 'New IP', new_device: 'New device', new_country: 'New country' };

export const SecurityFlagsBadge = ({ flags, country, city }) => {
  if (!Array.isArray(flags) || flags.length === 0) return null;
  const countryLabel = country ? `${country}${city ? ` — ${city}` : ''}` : null;
  const text = flags
    .map((f) => (f === 'new_country' && countryLabel ? `New country (${countryLabel})` : SECURITY_FLAG_LABELS[f] || f))
    .join(' · ');
  return (
    <Badge
      className="bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700 text-[10px] px-1.5 py-0 gap-1"
      data-testid="suspicious-login-badge"
    >
      <ShieldAlert className="w-3 h-3" />
      {text}
    </Badge>
  );
};

export const AuditLogList = ({
  logs = [],
  formatTimestamp,
  loading = false,
  emptyText = 'No audit logs found',
  emptySubText = 'Activity will be recorded here',
  heightClass = 'max-h-[420px]',
  getUserLabel,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="audit-log-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <div className="text-center py-10 flex flex-col items-center justify-center text-muted-foreground" data-testid="audit-log-empty">
        <History className="w-8 h-8 mb-2 opacity-30" />
        <p className="text-sm">{emptyText}</p>
        <p className="text-xs">{emptySubText}</p>
      </div>
    );
  }

  return (
    <div className={cn("overflow-y-auto", heightClass)} data-testid="audit-log-list">
      <div className="space-y-2 pr-2">
        {logs.map((log) => {
          const { Icon, cls } = iconMeta(log.action);
          return (
            <div
              key={log.id}
              className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50"
              data-testid={`audit-log-row-${log.id}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cls}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1 gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-medium text-sm">{getAuditActionLabel(log.action)}</p>
                    <SecurityFlagsBadge flags={log.security_flags} country={log.security_country} city={log.security_city} />
                  </div>
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    {formatTimestamp ? formatTimestamp(log.timestamp || log.created_at) : (log.timestamp || log.created_at)}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground break-words">{log.details || '-'}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  {getUserLabel && getUserLabel(log) && <span className="font-medium text-foreground/80">{getUserLabel(log)}</span>}
                  <span>By: {log.actor_email || 'System'}</span>
                  {log.ip_address && <span>IP: {log.ip_address}{log.ip_location ? <span className="text-foreground/70"> · {log.ip_location}</span> : ''}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AuditLogList;
